<template>
  <article
    :class="[
      'group relative flex flex-col gap-2 rounded-theme-md border p-3 transition',
      'border-theme-border bg-theme-surface hover:border-theme-brand/45 hover:shadow-theme-card',
    ]"
  >
    <header class="flex items-start justify-between gap-2 min-w-0">
      <div class="min-w-0">
        <h4 class="truncate text-sm font-medium text-theme-text">
          {{ title }}
        </h4>
        <p v-if="subtitle" class="truncate text-xs text-theme-text-muted">
          {{ subtitle }}
        </p>
      </div>
      <span
        v-if="periodLabel"
        class="inline-flex shrink-0 items-center rounded-full bg-theme-bg-subtle px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-theme-text-muted"
      >
        {{ periodLabel }}
      </span>
    </header>

    <!-- Inline chart (default slot) — caller renders the actual chart component. -->
    <div class="relative min-h-[6rem]">
      <slot>
        <sparkline-chart
          v-if="sparkline && sparkline.length"
          :data="sparkline"
          :height="64"
          :color="sparklineColor"
          show-area
        />
        <app-empty-state
          v-else
          compact
          :title="t('ui.chart_card_no_data')"
        />
      </slot>
    </div>

    <!-- Hover actions -->
    <div class="absolute top-2 right-2 hidden gap-1 group-hover:flex">
      <AppButton
        v-if="$attrs.onExpand"
        type="button"
        variant="ghost"
        tone="muted"
        size="xs"
        icon-only
        :icon="mdiArrowExpand"
        :aria-label="t('ui.chart_card_expand')"
        @click.stop="$emit('expand')"
      />
      <AppButton
        v-if="$attrs.onTogglePin"
        type="button"
        variant="ghost"
        :tone="pinned ? 'primary' : 'muted'"
        size="xs"
        icon-only
        :icon="pinned ? mdiPin : mdiPinOutline"
        :aria-label="pinned ? t('ui.chart_card_unpin') : t('ui.chart_card_pin')"
        @click.stop="$emit('toggle-pin', !pinned)"
      />
      <AppButton
        v-if="$attrs.onRemove"
        type="button"
        variant="ghost"
        tone="danger"
        size="xs"
        icon-only
        :icon="mdiClose"
        :aria-label="t('ui.chart_card_remove')"
        @click.stop="$emit('remove')"
      />
    </div>
  </article>
</template>

<script setup>
  /* eslint-disable no-undef */
  import AppButton from '@/components/ui/buttons/AppButton.vue';
  import AppEmptyState from '@/components/ui/states/AppEmptyState.vue';
  import SparklineChart from '@/components/ui/data-display/SparklineChart.vue';
  import { TREND_UP_COLOR } from './trendDirectionPalette';
  import {
    mdiArrowExpand,
    mdiClose,
    mdiPin,
    mdiPinOutline,
  } from '@mdi/js';
  import { useI18n } from 'vue-i18n';

  defineProps({
    title: { type: String, required: true },
    subtitle: { type: String, default: '' },
    periodLabel: { type: String, default: '' },
    pinned: { type: Boolean, default: false },
    /** Optional fallback sparkline data when the default slot is empty. */
    sparkline: { type: Array, default: () => [] },
    sparklineColor: { type: String, default: TREND_UP_COLOR },
  });

  defineEmits(['expand', 'toggle-pin', 'remove']);
  const { t } = useI18n();
</script>
